import { useCallback, useEffect, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type {
  AppliedFilter,
  FilterArrayItem,
  ListItem,
  ShipmentFiltersPayload,
} from '../types';

type LegType = 'CL' | 'EL';

type StatusFilterSnapshot = {
  appliedFilters: AppliedFilter[];
  filterArray: FilterArrayItem[];
};

type SnapshotByLeg = Partial<Record<LegType, StatusFilterSnapshot>>;

type SetState<T> = Dispatch<SetStateAction<T>>;

interface UseLegScopedProgressFilterArgs {
  activeLegType: LegType;
  appliedFilters: AppliedFilter[];
  filterArray: FilterArrayItem[];
  statusOptionsByLeg: Partial<Record<LegType, ListItem[]>>;
  progressFiltersByLeg?: SnapshotByLeg;
  setAppliedFilters: SetState<AppliedFilter[]>;
  setFilterArray: SetState<FilterArrayItem[]>;
  setProgressFiltersByLeg?: SetState<SnapshotByLeg>;
}

interface ProgressFilterState {
  appliedFilters: AppliedFilter[];
  filterArray: FilterArrayItem[];
}

const STATUS_FILTER_KEY = 'status';

const isStatusAppliedFilter = (filter: AppliedFilter) =>
  filter.filterBy === STATUS_FILTER_KEY ||
  filter.dateFilter === STATUS_FILTER_KEY;

const getStatusSnapshot = (
  appliedFilters: AppliedFilter[],
  filterArray: FilterArrayItem[]
): StatusFilterSnapshot => ({
  appliedFilters: appliedFilters.filter(isStatusAppliedFilter),
  filterArray: filterArray.filter(
    (filter) => filter.filterBy === STATUS_FILTER_KEY
  ),
});

const withoutStatusAppliedFilters = (filters: AppliedFilter[]) =>
  filters.filter((filter) => !isStatusAppliedFilter(filter));

const withoutStatusFilterArray = (filters: FilterArrayItem[]) =>
  filters.filter((filter) => filter.filterBy !== STATUS_FILTER_KEY);

const asArray = (value: FilterArrayItem['filterValue']) =>
  Array.isArray(value) ? value : [value];

const CL_ONLY_STATUSES = new Set(['INCOMPLETE', 'incomplete']);
const EL_ONLY_STATUSES = new Set(['BOOKED', 'booked']);

const stripIncompatibleLegStatuses = (
  snapshot: StatusFilterSnapshot,
  legType: LegType
): StatusFilterSnapshot => {
  const isInvalid = (id: string) => {
    const lower = id.toLowerCase();
    if (
      legType === 'EL' &&
      (CL_ONLY_STATUSES.has(id) || CL_ONLY_STATUSES.has(lower))
    ) {
      return true;
    }
    if (
      legType === 'CL' &&
      (EL_ONLY_STATUSES.has(id) || EL_ONLY_STATUSES.has(lower))
    ) {
      return true;
    }
    return false;
  };

  return {
    appliedFilters: snapshot.appliedFilters.filter(
      (filter) => !isInvalid(String(filter.id))
    ),
    filterArray: snapshot.filterArray
      .map((filter) => ({
        ...filter,
        filterValue: asArray(filter.filterValue).filter(
          (value) => !isInvalid(String(value))
        ),
      }))
      .filter((filter) => filter.filterValue.length > 0),
  };
};

const getValidStatusIds = (options: ListItem[]) =>
  new Set(
    (options || [])
      .filter((option) => option.enabled !== false)
      .map((option) => String(option.id))
  );

const filterSnapshotByValidStatuses = (
  snapshot: StatusFilterSnapshot,
  statusOptions: ListItem[]
): StatusFilterSnapshot => {
  const validIds = getValidStatusIds(statusOptions);
  if (validIds.size === 0) {
    return snapshot;
  }

  return {
    appliedFilters: snapshot.appliedFilters.filter((filter) =>
      validIds.has(String(filter.id))
    ),
    filterArray: snapshot.filterArray
      .map((filter) => ({
        ...filter,
        filterValue: asArray(filter.filterValue).filter((value) =>
          validIds.has(String(value))
        ),
      }))
      .filter((filter) => filter.filterValue.length > 0),
  };
};

const mergeStatusSnapshots = (
  primary: StatusFilterSnapshot,
  secondary: StatusFilterSnapshot
): StatusFilterSnapshot => {
  const appliedByKey = new Map<string, AppliedFilter>();
  [...primary.appliedFilters, ...secondary.appliedFilters].forEach((filter) => {
    appliedByKey.set(String(filter.id), filter);
  });

  const valuesByKey = new Map<string, FilterArrayItem>();
  [...primary.filterArray, ...secondary.filterArray].forEach((filter) => {
    const existing = valuesByKey.get(filter.filterBy);
    const existingValues = existing ? asArray(existing.filterValue) : [];
    const nextValues = asArray(filter.filterValue);

    valuesByKey.set(filter.filterBy, {
      ...existing,
      ...filter,
      filterValue: Array.from(
        new Set(
          [...existingValues, ...nextValues].map((value) => String(value))
        )
      ),
    });
  });

  return {
    appliedFilters: Array.from(appliedByKey.values()),
    filterArray: Array.from(valuesByKey.values()),
  };
};

const mergeStatusSnapshot = (
  appliedFilters: AppliedFilter[],
  filterArray: FilterArrayItem[],
  snapshot: StatusFilterSnapshot
) => ({
  appliedFilters: [
    ...withoutStatusAppliedFilters(appliedFilters),
    ...snapshot.appliedFilters,
  ],
  filterArray: [
    ...withoutStatusFilterArray(filterArray),
    ...snapshot.filterArray,
  ],
});

const areSnapshotsEqual = (
  first?: StatusFilterSnapshot,
  second?: StatusFilterSnapshot
) => JSON.stringify(first ?? {}) === JSON.stringify(second ?? {});

const isLegExclusiveStatus = (id: string, legType: LegType) => {
  const lower = id.toLowerCase();
  if (legType === 'EL') {
    return EL_ONLY_STATUSES.has(id) || EL_ONLY_STATUSES.has(lower);
  }
  return CL_ONLY_STATUSES.has(id) || CL_ONLY_STATUSES.has(lower);
};

const syncLegSnapshots = (
  activeLegType: LegType,
  currentSnapshot: StatusFilterSnapshot,
  existingSnapshots: SnapshotByLeg
): SnapshotByLeg => {
  const otherLegType: LegType = activeLegType === 'CL' ? 'EL' : 'CL';
  const existingOtherSnapshot = existingSnapshots[otherLegType] ?? {
    appliedFilters: [],
    filterArray: [],
  };

  const otherExclusiveApplied = existingOtherSnapshot.appliedFilters.filter(
    (f) => isLegExclusiveStatus(String(f.id), otherLegType)
  );
  const otherExclusiveFilterArray = existingOtherSnapshot.filterArray
    .map((f) => ({
      ...f,
      filterValue: asArray(f.filterValue).filter((v) =>
        isLegExclusiveStatus(String(v), otherLegType)
      ),
    }))
    .filter((f) => f.filterValue.length > 0);

  const commonSnapshot = stripIncompatibleLegStatuses(
    currentSnapshot,
    otherLegType
  );

  const updatedOtherSnapshot = mergeStatusSnapshots(
    {
      appliedFilters: otherExclusiveApplied,
      filterArray: otherExclusiveFilterArray,
    },
    commonSnapshot
  );

  return {
    ...existingSnapshots,
    [activeLegType]: currentSnapshot,
    [otherLegType]: updatedOtherSnapshot,
  };
};

export const useLegScopedProgressFilter = ({
  activeLegType,
  appliedFilters,
  filterArray,
  statusOptionsByLeg,
  progressFiltersByLeg,
  setAppliedFilters,
  setFilterArray,
  setProgressFiltersByLeg,
}: UseLegScopedProgressFilterArgs) => {
  const snapshotsByLegRef = useRef<SnapshotByLeg>(progressFiltersByLeg ?? {});
  const statusOptionsByLegRef = useRef(statusOptionsByLeg);

  useEffect(() => {
    statusOptionsByLegRef.current = statusOptionsByLeg;
  }, [statusOptionsByLeg]);

  useEffect(() => {
    snapshotsByLegRef.current = {
      ...snapshotsByLegRef.current,
      ...(progressFiltersByLeg ?? {}),
    };
  }, [progressFiltersByLeg]);

  const rememberStatusSnapshot = useCallback(
    (legType: LegType, snapshot: StatusFilterSnapshot) => {
      const nextSnapshots = syncLegSnapshots(
        legType,
        snapshot,
        snapshotsByLegRef.current
      );

      if (
        areSnapshotsEqual(
          snapshotsByLegRef.current[legType],
          nextSnapshots[legType]
        ) &&
        areSnapshotsEqual(
          snapshotsByLegRef.current[legType === 'CL' ? 'EL' : 'CL'],
          nextSnapshots[legType === 'CL' ? 'EL' : 'CL']
        )
      ) {
        return;
      }

      snapshotsByLegRef.current = nextSnapshots;

      setProgressFiltersByLeg?.((previousSnapshots) => ({
        ...(previousSnapshots ?? {}),
        ...nextSnapshots,
      }));
    },
    [setProgressFiltersByLeg]
  );

  const getStatusOptionsForLeg = useCallback((legType: LegType) => {
    return statusOptionsByLegRef.current[legType] ?? [];
  }, []);

  const sanitizeProgressFiltersForLeg = useCallback(
    (legType: LegType, filters: ProgressFilterState): ProgressFilterState => {
      const currentSnapshot = getStatusSnapshot(
        filters.appliedFilters,
        filters.filterArray
      );
      const legSanitizedSnapshot = stripIncompatibleLegStatuses(
        currentSnapshot,
        legType
      );

      let finalSnapshot = legSanitizedSnapshot;
      const statusOptions = getStatusOptionsForLeg(legType);

      if (statusOptions && statusOptions.length > 0) {
        finalSnapshot = filterSnapshotByValidStatuses(
          legSanitizedSnapshot,
          statusOptions
        );
      }

      return mergeStatusSnapshot(
        filters.appliedFilters,
        filters.filterArray,
        finalSnapshot
      ) as ProgressFilterState;
    },
    [getStatusOptionsForLeg]
  );

  const sanitizeProgressPayloadForLeg = useCallback(
    (
      legType: LegType,
      payload: ShipmentFiltersPayload | null
    ): ShipmentFiltersPayload | null => {
      if (!payload || typeof payload !== 'object' || !('status' in payload)) {
        return payload;
      }

      const statusOptions = getStatusOptionsForLeg(legType);
      const validIds = statusOptions.length
        ? getValidStatusIds(statusOptions)
        : null;

      const isInvalid = (id: string) => {
        const lower = id.toLowerCase();
        if (
          legType === 'EL' &&
          (CL_ONLY_STATUSES.has(id) || CL_ONLY_STATUSES.has(lower))
        )
          return true;
        if (
          legType === 'CL' &&
          (EL_ONLY_STATUSES.has(id) || EL_ONLY_STATUSES.has(lower))
        )
          return true;
        if (validIds && !validIds.has(id)) return true;
        return false;
      };

      const statusPayload = payload.status;
      const statusValues =
        statusPayload &&
        typeof statusPayload === 'object' &&
        !Array.isArray(statusPayload) &&
        'values' in statusPayload &&
        Array.isArray((statusPayload as { values?: unknown }).values)
          ? (statusPayload as { values: unknown[] }).values
          : Array.isArray(statusPayload)
            ? statusPayload
            : [];

      const nextValues = statusValues
        .map((value) => String(value))
        .filter((value) => !isInvalid(value));

      const nextPayload = { ...payload };
      if (nextValues.length === 0) {
        delete nextPayload.status;
      } else if (
        statusPayload &&
        typeof statusPayload === 'object' &&
        !Array.isArray(statusPayload) &&
        'values' in statusPayload
      ) {
        nextPayload.status = { ...statusPayload, values: nextValues };
      } else {
        nextPayload.status = nextValues;
      }

      return Object.keys(nextPayload).length ? nextPayload : null;
    },
    [getStatusOptionsForLeg]
  );

  const prepareProgressFiltersForLeg = useCallback(
    (nextLegType: LegType) => {
      const currentSnapshot = getStatusSnapshot(appliedFilters, filterArray);
      rememberStatusSnapshot(activeLegType, currentSnapshot);

      const targetSnapshot = mergeStatusSnapshots(
        snapshotsByLegRef.current[nextLegType] ?? {
          appliedFilters: [],
          filterArray: [],
        },
        currentSnapshot
      );

      const nextFilters = sanitizeProgressFiltersForLeg(nextLegType, {
        appliedFilters: [
          ...withoutStatusAppliedFilters(appliedFilters),
          ...targetSnapshot.appliedFilters,
        ],
        filterArray: [
          ...withoutStatusFilterArray(filterArray),
          ...targetSnapshot.filterArray,
        ],
      });

      setAppliedFilters(nextFilters.appliedFilters);
      setFilterArray(nextFilters.filterArray);
      return nextFilters;
    },
    [
      activeLegType,
      appliedFilters,
      filterArray,
      rememberStatusSnapshot,
      sanitizeProgressFiltersForLeg,
      setAppliedFilters,
      setFilterArray,
    ]
  );

  return {
    prepareProgressFiltersForLeg,
    sanitizeProgressFiltersForLeg,
    sanitizeProgressPayloadForLeg,
  };
};
